import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  FileText,
  Plus,
  Search,
  Loader2,
  CheckCircle,
  XCircle,
  Clock,
  Trophy,
  Medal,
  Award,
  Users,
  Filter,
  ChevronDown,
  ChevronUp,
  TrendingUp,
  BarChart3,
  RefreshCw,
  Beaker,
  ArrowRight,
  Target
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Progress } from "../components/ui/progress";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { ScrollArea } from "../components/ui/scroll-area";
import { cn } from "../lib/utils";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PHASES = ["Phase I", "Phase II", "Phase III", "Phase IV"];

const DECISION_STYLES = {
  Pending: { bg: "bg-amber-100", text: "text-amber-700", icon: Clock },
  Selected: { bg: "bg-green-100", text: "text-green-700", icon: CheckCircle },
  "Not Selected": { bg: "bg-red-100", text: "text-red-700", icon: XCircle },
};

const formatDate = (d) => {
  if (!d) return "—";
  try {
    return new Date(d.includes("T") ? d : d + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  } catch { return d; }
};

export default function Feasibilities() {
  const [feasibilities, setFeasibilities] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [showDetail, setShowDetail] = useState(null);
  const [showDecision, setShowDecision] = useState(null);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [creating, setCreating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [contactSearch, setContactSearch] = useState("");
  const [contactResults, setContactResults] = useState([]);
  const [winReasons, setWinReasons] = useState([]);
  const [lossReasons, setLossReasons] = useState([]);

  const user = JSON.parse(localStorage.getItem("stryde_user") || "{}");

  const emptyForm = {
    linked_contact_ids: [],
    protocol_number: "",
    sponsor: "",
    study_name: "",
    indication: "",
    therapeutic_area: "",
    phase: "",
    site: "",
    pi_proposed: "",
    enrollment_target: "",
    enrollment_projection: "",
    notes: "",
  };
  const [form, setForm] = useState(emptyForm);
  const [selectedContacts, setSelectedContacts] = useState([]);

  // Decision form
  const [decisionForm, setDecisionForm] = useState({
    decision_outcome: "",
    win_reason: "",
    loss_reason: "",
  });

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter !== "all") params.append("status", statusFilter);
      if (search) params.append("search", search);
      const [feasRes, statsRes, reasonsRes] = await Promise.all([
        axios.get(`${API}/feasibilities?${params.toString()}`),
        axios.get(`${API}/feasibilities/stats`),
        axios.get(`${API}/feasibilities/reasons`),
      ]);
      setFeasibilities(feasRes.data);
      setStats(statsRes.data);
      setWinReasons(reasonsRes.data.win_reasons);
      setLossReasons(reasonsRes.data.loss_reasons);
    } catch (error) {
      toast.error("Failed to load feasibilities");
    } finally {
      setLoading(false);
    }
  }, [statusFilter, search]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const searchContacts = async (q) => {
    if (!q || q.length < 2) { setContactResults([]); return; }
    try {
      const res = await axios.get(`${API}/leads?search=${encodeURIComponent(q)}&limit=10`);
      setContactResults(res.data || []);
    } catch { setContactResults([]); }
  };

  const addContact = (contact) => {
    if (selectedContacts.find(c => c.id === contact.id)) return;
    setSelectedContacts(prev => [...prev, contact]);
    setForm(prev => ({ ...prev, linked_contact_ids: [...prev.linked_contact_ids, contact.id] }));
    setContactSearch("");
    setContactResults([]);
  };

  const removeContact = (id) => {
    setSelectedContacts(prev => prev.filter(c => c.id !== id));
    setForm(prev => ({ ...prev, linked_contact_ids: prev.linked_contact_ids.filter(i => i !== id) }));
  };

  const createFeasibility = async () => {
    if (!form.sponsor && !form.study_name) {
      toast.error("Please provide a sponsor or study name");
      return;
    }
    setCreating(true);
    try {
      const payload = {
        ...form,
        enrollment_target: form.enrollment_target ? parseInt(form.enrollment_target) : null,
        enrollment_projection: form.enrollment_projection ? parseInt(form.enrollment_projection) : null,
        created_by: user.name || "System",
      };
      await axios.post(`${API}/feasibilities`, payload);
      toast.success("Feasibility created. Linked contacts moved to 'Feasibility Out'.");
      setShowCreate(false);
      setForm(emptyForm);
      setSelectedContacts([]);
      fetchAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create feasibility");
    } finally {
      setCreating(false);
    }
  };

  const submitDecision = async () => {
    if (!decisionForm.decision_outcome) {
      toast.error("Select a decision outcome");
      return;
    }
    if (decisionForm.decision_outcome === "Selected" && !decisionForm.win_reason) {
      toast.error("Win reason is required for Selected");
      return;
    }
    if (decisionForm.decision_outcome === "Not Selected" && !decisionForm.loss_reason) {
      toast.error("Loss reason is required for Not Selected");
      return;
    }
    setSaving(true);
    try {
      await axios.put(`${API}/feasibilities/${showDecision.id}`, decisionForm);
      toast.success(`Decision recorded: ${decisionForm.decision_outcome}`);
      setShowDecision(null);
      fetchAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save decision");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="feasibilities-loading">
        <Loader2 className="h-8 w-8 animate-spin text-[#2E7AD1]" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="feasibilities-page">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-[#1C2A25] font-['Outfit'] tracking-tight">Feasibilities</h1>
          <p className="text-slate-500 text-sm mt-0.5">Track feasibility submissions, decisions, and win/loss reasons</p>
        </div>
        <Button onClick={() => setShowCreate(true)} data-testid="create-feasibility-btn">
          <Plus className="h-4 w-4 mr-1" /> New Feasibility
        </Button>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
          <Card><CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-slate-900">{stats.total}</p>
            <p className="text-xs text-slate-500">Total</p>
          </CardContent></Card>
          <Card><CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-amber-600">{stats.pending}</p>
            <p className="text-xs text-slate-500">Pending</p>
          </CardContent></Card>
          <Card><CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-green-600">{stats.selected}</p>
            <p className="text-xs text-slate-500">Selected</p>
          </CardContent></Card>
          <Card><CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-red-600">{stats.not_selected}</p>
            <p className="text-xs text-slate-500">Not Selected</p>
          </CardContent></Card>
          <Card><CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-blue-600">{stats.win_rate}%</p>
            <p className="text-xs text-slate-500">Win Rate</p>
          </CardContent></Card>
          <Card><CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-purple-600">{stats.avg_days_to_decision}</p>
            <p className="text-xs text-slate-500">Avg Days to Decision</p>
          </CardContent></Card>
        </div>
      )}

      {/* Win/Loss Reason Charts */}
      {stats && (stats.win_reasons.length > 0 || stats.loss_reasons.length > 0) && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {stats.win_reasons.length > 0 && (
            <Card data-testid="win-reasons-chart">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-semibold flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 text-green-500" /> Win Reasons
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {stats.win_reasons.map((wr, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <span className="text-xs text-slate-600 w-40 truncate">{wr.reason}</span>
                      <Progress value={(wr.count / Math.max(...stats.win_reasons.map(r => r.count))) * 100} className="h-2 flex-1" />
                      <span className="text-xs font-bold text-green-700 w-6 text-right">{wr.count}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
          {stats.loss_reasons.length > 0 && (
            <Card data-testid="loss-reasons-chart">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-semibold flex items-center gap-2">
                  <XCircle className="h-4 w-4 text-red-500" /> Loss Reasons
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {stats.loss_reasons.map((lr, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <span className="text-xs text-slate-600 w-40 truncate">{lr.reason}</span>
                      <Progress value={(lr.count / Math.max(...stats.loss_reasons.map(r => r.count))) * 100} className="h-2 flex-1" />
                      <span className="text-xs font-bold text-red-700 w-6 text-right">{lr.count}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex gap-3 flex-wrap">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input placeholder="Search feasibilities..." value={search} onChange={e => setSearch(e.target.value)} className="pl-10" data-testid="feasibility-search" />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-40" data-testid="feasibility-status-filter">
                <SelectValue placeholder="All Statuses" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="Pending">Pending</SelectItem>
                <SelectItem value="Selected">Selected</SelectItem>
                <SelectItem value="Not Selected">Not Selected</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline" size="sm" onClick={fetchAll}><RefreshCw className="h-4 w-4" /></Button>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Sponsor / Study</TableHead>
                <TableHead>Protocol</TableHead>
                <TableHead>Indication</TableHead>
                <TableHead>Phase</TableHead>
                <TableHead>PI</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Contacts</TableHead>
                <TableHead>Created</TableHead>
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {feasibilities.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} className="text-center py-12 text-slate-400">
                    <Beaker className="h-10 w-10 mx-auto mb-2 text-slate-300" />
                    <p>No feasibilities yet. Click "New Feasibility" to create one.</p>
                  </TableCell>
                </TableRow>
              ) : (
                feasibilities.map(f => {
                  const ds = DECISION_STYLES[f.decision_outcome] || DECISION_STYLES.Pending;
                  const DIcon = ds.icon;
                  return (
                    <TableRow key={f.id} className="cursor-pointer hover:bg-slate-50" onClick={() => setShowDetail(f)} data-testid={`feasibility-row-${f.id}`}>
                      <TableCell>
                        <div>
                          <p className="font-medium text-slate-900">{f.sponsor || "—"}</p>
                          <p className="text-xs text-slate-500">{f.study_name || "—"}</p>
                        </div>
                      </TableCell>
                      <TableCell className="text-sm">{f.protocol_number || "—"}</TableCell>
                      <TableCell className="text-sm">{f.indication || "—"}</TableCell>
                      <TableCell className="text-sm">{f.phase || "—"}</TableCell>
                      <TableCell className="text-sm">{f.pi_proposed || "—"}</TableCell>
                      <TableCell>
                        <Badge className={cn("gap-1", ds.bg, ds.text, "border-0")}>
                          <DIcon className="h-3 w-3" /> {f.decision_outcome || "Pending"}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge variant="secondary" className="text-xs">{f.linked_contact_ids?.length || 0}</Badge>
                      </TableCell>
                      <TableCell className="text-xs text-slate-500">{formatDate(f.created_at)}</TableCell>
                      <TableCell>
                        {f.decision_outcome === "Pending" && (
                          <Button size="sm" variant="outline" onClick={(e) => {
                            e.stopPropagation();
                            setShowDecision(f);
                            setDecisionForm({ decision_outcome: "", win_reason: "", loss_reason: "" });
                          }} data-testid={`record-decision-${f.id}`}>
                            Record Decision
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Create Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>New Feasibility Submission</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Sponsor *</Label>
                <Input value={form.sponsor} onChange={e => setForm(p => ({ ...p, sponsor: e.target.value }))} placeholder="e.g., Pfizer" data-testid="feas-sponsor" />
              </div>
              <div className="space-y-2">
                <Label>Study Name</Label>
                <Input value={form.study_name} onChange={e => setForm(p => ({ ...p, study_name: e.target.value }))} placeholder="Study name" data-testid="feas-study" />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Protocol #</Label>
                <Input value={form.protocol_number} onChange={e => setForm(p => ({ ...p, protocol_number: e.target.value }))} data-testid="feas-protocol" />
              </div>
              <div className="space-y-2">
                <Label>Indication</Label>
                <Input value={form.indication} onChange={e => setForm(p => ({ ...p, indication: e.target.value }))} data-testid="feas-indication" />
              </div>
              <div className="space-y-2">
                <Label>Phase</Label>
                <Select value={form.phase} onValueChange={v => setForm(p => ({ ...p, phase: v }))}>
                  <SelectTrigger data-testid="feas-phase"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>
                    {PHASES.map(ph => <SelectItem key={ph} value={ph}>{ph}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Site</Label>
                <Input value={form.site} onChange={e => setForm(p => ({ ...p, site: e.target.value }))} data-testid="feas-site" />
              </div>
              <div className="space-y-2">
                <Label>PI Proposed</Label>
                <Input value={form.pi_proposed} onChange={e => setForm(p => ({ ...p, pi_proposed: e.target.value }))} data-testid="feas-pi" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Enrollment Target</Label>
                <Input type="number" value={form.enrollment_target} onChange={e => setForm(p => ({ ...p, enrollment_target: e.target.value }))} data-testid="feas-target" />
              </div>
              <div className="space-y-2">
                <Label>Enrollment Projection</Label>
                <Input type="number" value={form.enrollment_projection} onChange={e => setForm(p => ({ ...p, enrollment_projection: e.target.value }))} data-testid="feas-projection" />
              </div>
            </div>

            {/* Link Contacts */}
            <div className="space-y-2">
              <Label>Link Contacts</Label>
              <div className="relative">
                <Input
                  placeholder="Search contacts to link..."
                  value={contactSearch}
                  onChange={e => { setContactSearch(e.target.value); searchContacts(e.target.value); }}
                  data-testid="feas-contact-search"
                />
                {contactResults.length > 0 && (
                  <div className="absolute z-50 w-full mt-1 bg-white border rounded-md shadow-lg max-h-40 overflow-auto">
                    {contactResults.map(c => (
                      <div key={c.id} className="px-3 py-2 hover:bg-slate-100 cursor-pointer text-sm flex justify-between" onClick={() => addContact(c)}>
                        <span>{c.contact || c.company || c.email}</span>
                        <span className="text-xs text-slate-400">{c.company}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              {selectedContacts.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-1">
                  {selectedContacts.map(c => (
                    <Badge key={c.id} variant="secondary" className="gap-1">
                      {c.contact || c.company}
                      <button onClick={() => removeContact(c.id)} className="ml-1 text-slate-400 hover:text-red-500">&times;</button>
                    </Badge>
                  ))}
                </div>
              )}
            </div>

            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea value={form.notes} onChange={e => setForm(p => ({ ...p, notes: e.target.value }))} rows={2} data-testid="feas-notes" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button>
            <Button onClick={createFeasibility} disabled={creating} data-testid="submit-feasibility">
              {creating ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Plus className="h-4 w-4 mr-1" />}
              Create Feasibility
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detail Dialog */}
      {showDetail && (
        <Dialog open={!!showDetail} onOpenChange={() => setShowDetail(null)}>
          <DialogContent className="max-w-xl max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{showDetail.sponsor || "Feasibility"} — {showDetail.study_name || showDetail.protocol_number || "Details"}</DialogTitle>
            </DialogHeader>
            <div className="space-y-3 text-sm">
              <div className="grid grid-cols-2 gap-3">
                <div><span className="text-slate-500">Protocol:</span> <span className="font-medium">{showDetail.protocol_number || "—"}</span></div>
                <div><span className="text-slate-500">Phase:</span> <span className="font-medium">{showDetail.phase || "—"}</span></div>
                <div><span className="text-slate-500">Indication:</span> <span className="font-medium">{showDetail.indication || "—"}</span></div>
                <div><span className="text-slate-500">Site:</span> <span className="font-medium">{showDetail.site || "—"}</span></div>
                <div><span className="text-slate-500">PI:</span> <span className="font-medium">{showDetail.pi_proposed || "—"}</span></div>
                <div><span className="text-slate-500">Enrollment Target:</span> <span className="font-medium">{showDetail.enrollment_target || "—"}</span></div>
                <div><span className="text-slate-500">Enrollment Projection:</span> <span className="font-medium">{showDetail.enrollment_projection || "—"}</span></div>
              </div>
              {(() => {
                const ds = DECISION_STYLES[showDetail.decision_outcome] || DECISION_STYLES.Pending;
                const DIcon = ds.icon;
                return (
                  <div className="flex items-center gap-2">
                    <span className="text-slate-500">Decision:</span>
                    <Badge className={cn("gap-1", ds.bg, ds.text, "border-0")}><DIcon className="h-3 w-3" /> {showDetail.decision_outcome}</Badge>
                    {showDetail.win_reason && <Badge variant="outline" className="text-xs bg-green-50 text-green-700">{showDetail.win_reason}</Badge>}
                    {showDetail.loss_reason && <Badge variant="outline" className="text-xs bg-red-50 text-red-700">{showDetail.loss_reason}</Badge>}
                  </div>
                );
              })()}
              {showDetail.notes && <div><span className="text-slate-500">Notes:</span> <p className="mt-1 text-slate-700">{showDetail.notes}</p></div>}
              <div className="text-xs text-slate-400">
                Created: {formatDate(showDetail.created_at)} by {showDetail.created_by || "—"}
                {showDetail.decision_date && <> | Decision: {formatDate(showDetail.decision_date)}</>}
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Decision Dialog */}
      {showDecision && (
        <Dialog open={!!showDecision} onOpenChange={() => setShowDecision(null)}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Record Decision</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <p className="text-sm text-slate-600">
                {showDecision.sponsor} — {showDecision.study_name || showDecision.protocol_number}
              </p>
              <div className="space-y-2">
                <Label>Decision Outcome *</Label>
                <Select value={decisionForm.decision_outcome} onValueChange={v => setDecisionForm(p => ({ ...p, decision_outcome: v }))}>
                  <SelectTrigger data-testid="decision-outcome"><SelectValue placeholder="Select decision" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Selected">Selected (Win)</SelectItem>
                    <SelectItem value="Not Selected">Not Selected (Loss)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {decisionForm.decision_outcome === "Selected" && (
                <div className="space-y-2">
                  <Label>Win Reason *</Label>
                  <Select value={decisionForm.win_reason} onValueChange={v => setDecisionForm(p => ({ ...p, win_reason: v }))}>
                    <SelectTrigger data-testid="win-reason"><SelectValue placeholder="Select reason" /></SelectTrigger>
                    <SelectContent>
                      {winReasons.map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              )}
              {decisionForm.decision_outcome === "Not Selected" && (
                <div className="space-y-2">
                  <Label>Loss Reason *</Label>
                  <Select value={decisionForm.loss_reason} onValueChange={v => setDecisionForm(p => ({ ...p, loss_reason: v }))}>
                    <SelectTrigger data-testid="loss-reason"><SelectValue placeholder="Select reason" /></SelectTrigger>
                    <SelectContent>
                      {lossReasons.map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowDecision(null)}>Cancel</Button>
              <Button onClick={submitDecision} disabled={saving} data-testid="submit-decision">
                {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
                Submit Decision
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
